import React from "react"
import moment from "moment"
import store from "store"
import { Checkbox } from "react-bootstrap"
import { DckSelectors } from "dck-redux"

//////////////////// Utilities functions and Constants ///////////////////////////

const INVALID_TOKEN = 'invalid_token',

/** Maps frequencyType integer to string */
getFrequencyType = type => {
  switch(type) {
    case 0:
      return "Days"

    case 1:
      return "Weeks"

    case 2:
      return "Months"

    default:
      return "Years"
  }
},

LoanStatus = {
  NEW: 'NEW',
  UNDERWRITER_REVIEW: 'UNDERWRITER_REVIEW',
  UNDERWRITER_APPROVED: 'UNDERWRITER_APPROVED',
  UNDERWRITER_DECLINED: 'UNDERWRITER_DECLINED',
  APPROVER_REVIEW: 'APPROVER_REVIEW',
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED', 
  COMPLETED: 'COMPLETED'
},

UserRoles = {
  SUPER: 'SUPER',
  ADMIN: 'ADMIN',
  APPROVER: 'APPROVER',
  UNDERWRITER: 'UNDERWRITER',
  CREATOR: 'CREATOR',
  ACCOUNTANT: 'ACCOUNTANT',
  PAYMENT_MANAGER: 'PAYMENT_MANAGER',
},

HTTP_VERB = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
},

VALIDATION_TYPE = {
  SUCCESS: 'success',
  ERROR: 'error',
  NULL: null
},

fetchServer = (method, url, accessToken, body, isContentJSON, accept, isResource, contentType) => {

  const headers = {}
  headers['Accept'] = accept

  if(accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  if(contentType) {
    headers['Content-type'] = contentType
  }
  else if((method === HTTP_VERB.POST || method === HTTP_VERB.PUT) && isContentJSON) {
    headers['Content-type'] = 'application/json'
  }

  return fetch(url, {method, headers, body})
  .then(response => {

    if(!response.ok) {
      return response.text().then(text => { throw new Error(text) })
    }

    return isResource ? response.blob() : response.json()
  })
},

/** Fetch json content */
fetchJson = (method, url, accessToken, body) => 
  fetchServer(method, url, accessToken, body, true, 'application/json', false, null),

/** Fetch with form Data */
fetchFormData = (method, url, accessToken, body) =>
  fetchServer(method, url, accessToken, body, false, 'application/json', false, null),

/** Fetch resource */
fetchResource = (method, url, accessToken, body) => 
  fetchServer(method, url, accessToken, body, false, '*/*', true, null),

fetchUrlencoded = (method, url, accessToken, body) => 
  fetchServer(
    method, url, accessToken, body, false, 'application/json', true, 
    'application/x-www-form-urlencoded; charset=utf-8'
  ),

/** Parse fetch response to extract the error text and code */
parseFetchError = error => {

  let errorObj = {}
  try {
    errorObj = JSON.parse(error.message)
  }
  catch(e) {}

  let text = '',
    status = ''

  if(Object.hasOwnProperty.call(errorObj, 'body')) {
    text = errorObj.body
    status = errorObj.code
  }
  else if(Object.hasOwnProperty.call(errorObj, 'error')) {
    text = errorObj.error
    status = errorObj.status
  }
  else if(Object.hasOwnProperty.call(errorObj, 'message')) {
    text = errorObj.message
    status = errorObj.code
  }
  else {
    text = error.message ? error.message : 'An error occurred!'
    status = 500
  }

  text = text === 'access_denied' ? 'Access is denied' : text
  status = text === INVALID_TOKEN ? 401 : status

  return { text, status }
},

/** Color of Loan status */
statusColorStyle = cell => {

  let color
  switch(cell) {
    case LoanStatus.COMPLETED: 
      color = 'green'
      break
    case LoanStatus.UNDERWRITER_APPROVED:
      color = '#214849'
      break
    case LoanStatus.APPROVED:
      color = '#0e9e7a'
      break
    case LoanStatus.APPROVER_REVIEW: 
    case LoanStatus.UNDERWRITER_REVIEW: 
      color = '#676832'
      break
    case LoanStatus.UNDERWRITER_DECLINED: 
    case LoanStatus.DECLINED:
      color = '#e8654f'
      break
    default:
      color = '#222'
  }

  return color
},

formatCollateralName = name => name ? name.split('_').join(' ') : name,

LoanCollateral = {
  DEVICE: 'Device', 
  NO_COLLATERAL: 'No collateral',
},

LoanCollateralEnum = {
  DEVICE: 'DEVICE', 
  NO_COLLATERAL: 'NO_COLLATERAL',
},

getOrganization = () => store.get('organization'),

saveOrganization = organization => store.set('organization', organization),

/** Masks the email with asterisk chars */
maskEmail = email => email 
  ? `${email.substring(0, 3)}*****@*****${email.substring(email.length - 3, email.length)}` : '',

/** Checks if the given object is empty i.e obj === {} */
isEmptyObject = obj => {
  for(let i in obj) return false
  return true
},

/** Fills a zero in front of numbers less than 10 */
fillingZeroUtility = nb => (Number(nb) > 0 && Number(nb) < 10) ? `0${ Number(nb) }` : nb,

/** Render Loan Collateral */
renderLoanCollateral = checked => {

  const organization = getOrganization(),
  collaterals = organization ? organization.loanCollaterals : null
  if(!collaterals || !collaterals.length) {
    return null
  }

  return (
    <div>
    { collaterals.map((value, index) => (
      <span key={index}>
        <Checkbox readOnly inline checked={ value === checked }>
          { formatCollateralName(value) }
        </Checkbox> &nbsp; &nbsp;
      </span>
    ))}
    </div>
  )
},

getCustomerFullName = customer => customer ? customer.fullName : '',

getBusinessName = business => business ? business.name : '',

/** Cast from [2022, 5, 12] ==> 2022-05-12 */
fineractToDate = date => (!date || date.length !== 3)
  ? null
  : `${ date[0] }-${ fillingZeroUtility(date[1]) }-${ fillingZeroUtility(date[2]) }`,

/** Casts from [2022, 5, 12] ==> May 12, 2022 */
fineractDateToStr = date => {
  const temp = fineractToDate(date)
  return temp ? moment(temp).format('ll') : null
},

/** Lower camel case text */
lowerCamelCase = text => {
  if(!text) return text
  text = trimSpace(text)
  let textArray = text.split(' ')
  textArray = textArray.map((value, index) => index ? value.charAt(0).toUpperCase().concat(value.substring(1)) : value.toLowerCase())
  return textArray.join('')
},

/** EXAMPLE => Example */
capitalFirstCharacter = text => {
  if(!text) return text
  text = trimSpace(text)
  return text.charAt(0).toUpperCase().concat(text.length > 1 ? text.substring(1).toLowerCase() : '')
},

trimSpace = text => text ? String(text).trim() : text,

customerFieldsObject = {
  TEXT: 'TEXT',
  EMAIL: 'EMAIL',
  NUMBER: 'NUMBER',
  CHECKBOX: 'CHECKBOX',
  RADIO: 'RADIO',
  SELECT: 'SELECT',
  DATETIME: 'DATETIME',
  DATE: 'DATE',
  DATE_OF_BIRTH: 'DATE_OF_BIRTH'
},

customerImageType = {
  PHOTO: 'PHOTO',
  ID_SCAN: 'ID_SCAN',
  SIGNED_T_AND_C: 'SIGNED_T_AND_C',
  ADDRESS_SCAN: 'ADDRESS_SCAN',
  OTHER: 'OTHER',
},

isCustomerDateField = type => type === customerFieldsObject.DATE 
  || type === customerFieldsObject.DATETIME
  || type === customerFieldsObject.DATE_OF_BIRTH,

validateTextState = (text, nullAccepted) => {
  text = trimSpace(text)
  return (!text && nullAccepted) ? VALIDATION_TYPE.NULL : text ? VALIDATION_TYPE.SUCCESS : VALIDATION_TYPE.ERROR
},

validateAmountState = (amount, nullAccepted) =>
  (!amount && nullAccepted)
  ? VALIDATION_TYPE.NULL
  : (amount === '' || amount === undefined || amount === null || isNaN(amount) || amount < 0)
    ? VALIDATION_TYPE.ERROR
    : VALIDATION_TYPE.SUCCESS,

validatePercentState = (amount, nullAccepted) =>
  (!amount && nullAccepted)
  ? VALIDATION_TYPE.NULL
  : (amount === '' || amount === undefined || amount === null || isNaN(amount) || amount < 0 || amount > 100)
    ? VALIDATION_TYPE.ERROR
    : VALIDATION_TYPE.SUCCESS,

validateMinMaxState = (amount, min, max, nullAccepted) =>
  (!amount && nullAccepted)
  ? VALIDATION_TYPE.NULL
  : (amount === '' || amount === undefined || amount === null || isNaN(amount)
  || (min && amount < min) || (max && amount > max))
    ? VALIDATION_TYPE.ERROR 
    : VALIDATION_TYPE.SUCCESS,

validateDateState = (date, minDate) => {

  try {
    const choosedDate = moment(date).startOf('day'),
    now = moment().startOf('day'),
    minDateStr = moment(minDate).startOf('day')

    return choosedDate.isBetween(minDateStr, now, undefined, '[]')
      ? VALIDATION_TYPE.SUCCESS
      : VALIDATION_TYPE.ERROR
  }
  catch(e) {
    return VALIDATION_TYPE.ERROR
  }
},

validateCommissions = (data, nullAccepted) => 
  data.every(item => item.id) ? VALIDATION_TYPE.SUCCESS 
    : nullAccepted ? VALIDATION_TYPE.NULL : VALIDATION_TYPE.ERROR,

Currencies = [
  { value: 'USD', title: 'USD ($)' },
  { value: 'EUR', title: 'EUR (€)' },
  { value: 'RWF', title: 'Rwanda Franc' }
],

ReportAPI = {
  ACTIVE_LOAN: 'ACTIVE_LOAN',
  EXPECTED_PAYMENT: 'EXPECTED_PAYMENT',
  BALANCE_OUTSTANDING: 'BALANCE_OUTSTANDING',
  AGING_LOAN: 'AGING_LOAN',
  PORTFOLIO_AT_RISK: 'PORTFOLIO_AT_RISK',
  BRANCH_CASH_IN: 'BRANCH_CASH_IN',
  LOAN_GENERALITY: 'LOAN_GENERALITY',
  LOAN_DISBURSED: 'LOAN_DISBURSED',
  DEFAULTED_LOAN_DETAILS: 'DEFAULTED_LOAN_DETAILS',

  GENERAL_LEDGER: 'GENERAL_LEDGER',
  INCOME_STATEMENT: 'INCOME_STATEMENT',
  BALANCE_SHEET: 'BALANCE_SHEET',
  TRIAL_BALANCE: 'TRIAL_BALANCE',

  BATCH_PERFORMANCE: 'BATCH_PERFORMANCE',
  BUCKET_PERFORMANCE: 'BUCKET_PERFORMANCE',
},

getPercentAmount = (amount, percent) => {

  let r = null
  if(!isNaN(amount) && !isNaN(percent)) {
    const result = (percent * amount) / 100
    r = Math.round((result + Number.EPSILON) * 100) / 100
  }
  return r
},

getUpfrontPaymentAndCurrency = (bundleCost, upfrontPayment, currencyCode) => {
  const res = getPercentAmount(bundleCost, upfrontPayment)
  return `${res != null ? formatCurrency(res) : '_'} ${ currencyCode }`
},

Orientation = {
  PORTRAIT: 'PORTRAIT',
  LANDSCAPE: 'LANDSCAPE'
},

LoanRecordingStep = {
  BEGIN: 'BEGIN',
  ADD_LOAN_IMEI: 'ADD_LOAN_IMEI',
  UPDATE_FIRST_INSTALLMENT: 'UPDATE_FIRST_INSTALLMENT',
  APPROVE_LOAN: 'APPROVE_LOAN',
  DISBURSE_LOAN: 'DISBURSE_LOAN',
  SAVE_UPFRONT_PAYMENT: 'SAVE_UPFRONT_PAYMENT',
  DONE: 'DONE',
},

CustomerType = {
  INDIVIDUAL: 'INDIVIDUAL',
  BUSINESS: 'BUSINESS'
},

/** Plural form */
getPlural = (name, nb) => `${ name }${ nb > 1 ? 's' : ''}`,

ALL_STATUS = {
  key: 'ALL',
  value: 'All'
},

REJECTED_STATUS = {
  key: 'REJECTED',
  value: 'Rejected'
},

ACCEPTED_STATUS = {
  key: 'ACCEPTED',
  value: 'Accepted'
},

DEVICE_OP_TYPE = {
  LOCK: 'LOCK',
  UNLOCK: 'UNLOCK',
},

isValidPhoneNumber = phoneNumber => {
  const phoneNumberRegex = new RegExp("^[^-](\\+?)([0-9- ]){6,20}$")
  return phoneNumberRegex.test(phoneNumber.trim()) ? 'success' : 'error'
},

validatePhoneNumber = phoneNumber => !phoneNumber ? null : isValidPhoneNumber(phoneNumber),

getAccessToken = () => store.get("sessionData").access_token,

DATE_FORMAT_STR = 'YYYY-MM-DD',

DEFAULT_NB_DAYS_EXTENSION = 3,

/** Format Number as Currency */
formatCurrency = amount => (amount === null || amount === undefined) 
  ? null
  : new Intl.NumberFormat().format(amount),

formatCurrencyAlt = (amount, currency) =>
  new Intl.NumberFormat("en-US", { style: 'currency', currency }).format(amount),

ChargeTimeType = {
  1: 'Disbursement',
  2: 'Specified Due Date'
},

ChargeCalculationType = {
  1: 'Flat',
  2: '% of Amount'
},

getChargeAmount = ({amount, chargeCalculationType, currencyCode }) =>
  `${ formatCurrency(amount) } ${  chargeCalculationType === 2 ? '%' : currencyCode }`,

AccountUsage = { DETAIL: 'DETAIL', HEADER: 'HEADER' },

GLAccountType = {
  ASSET: 'ASSET',
  LIABILITY: 'LIABILITY',
  EQUITY: 'EQUITY',
  INCOME: 'INCOME',
  EXPENSE: 'EXPENSE'
},

GLAccountTypes = [
  { value: GLAccountType.ASSET, title: 'Asset' },
  { value: GLAccountType.LIABILITY, title: 'Liability' },
  { value: GLAccountType.EQUITY, title: 'Equity' },
  { value: GLAccountType.INCOME, title: 'Income' },
  { value: GLAccountType.EXPENSE, title: 'Expense' },
],

GLAccountUsages = [
  { value: AccountUsage.DETAIL, title: 'Detail' },
  { value: AccountUsage.HEADER, title: 'Header' },
],

saveDefaultCurrency = currencyCode => store.set("currency", currencyCode),

getDefaultCurrency = () => store.get("currency", Currencies[0].value),

CALCULATION_TYPE = {
  FLAT: 'FLAT',
  PERCENT: 'PERCENT'
},

filterGLAccounts = ({ usage, fineractId, disabled }) =>
  usage === AccountUsage.DETAIL && fineractId > 0 && !disabled,

filterGLAccountsByType = ({ type }, acceptedType) => type === acceptedType,

// Get IDs
glAccountsDataWithId = glAccounts => glAccounts
  .filter(filterGLAccounts)
  .map(({ id, name, glCode, type }) => ({ value: id, title: `${name} (${glCode})`, type })),

// Get fineractIds
glAccountsDataWithFineractId = glAccounts => glAccounts
  .filter(filterGLAccounts)
  .map(({ fineractId, name, glCode, type }) => ({ value: fineractId, title: `${name} (${glCode})`, type })),

TransactionEntryType = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT'
},

canUserSeeThisLoan = (status, isAdmin, isPaymentManager, isApprover, isUnderwriter, isAccountant) => {

  const okPaymentManager = isPaymentManager && status === LoanStatus.COMPLETED,

  okAccountant = isAccountant && status === LoanStatus.COMPLETED,

  okApprover = isApprover && (status === LoanStatus.UNDERWRITER_APPROVED || status === LoanStatus.APPROVER_REVIEW),

  okUnderwriter = isUnderwriter && (status === LoanStatus.NEW || status === LoanStatus.UNDERWRITER_REVIEW)

  return isAdmin || okAccountant || okPaymentManager || okApprover || okUnderwriter
},

addUniqueValue = (arr, value) => {
  if (value && !arr.includes(value)) {
    arr.push(value)
  }
},

/** Sorting dates */
sortDate = (a, b, order) => {

  const d1 = moment(a),
    d2 = moment(b)

  return order === 'asc' 
    ? (d1.unix() - d2.unix())
    : (d2.unix() - d1.unix())
},

/** Sorting numbers */
sortNumber = (a, b, order) => order === 'asc' ? a - b : b - a,

/** Convert Array to object */
convertToObject = arr => {
  const data = {}
  arr.sort().forEach(item => data[item] = item)
  return data
},

selectFiltersFromStore = (state, itemType) => {
  const data = DckSelectors.selectAllItems(state, itemType)
  return data.length ? data[0] : {}
},

BooleanStatuses = {
  true: 'Yes',
  false: 'No'
},

getAvatarUri = (name, size) =>
  `https://ui-avatars.com/api/?rounded=true&size=${size}&background=random&color=fff&name=${encodeURIComponent(name)}`,

saveFilterConds = data => store.set('LoanFilters', data),

getFilterConds = () => store.get('LoanFilters'),

businessDataWithId = businesses => businesses
  .map(({ id, name, businessId }) => ({ value: id, title: `${name} - (${businessId})` })),

batchDataWithId = batches => batches
  .map(({ id, name, affiliation }) => ({ value: id, title: `${name} (${affiliation ? affiliation.name : ''})`})),

bucketDataWithId = buckets => buckets
  .map(({ id, name, amount }) => ({ value: id, title: `${name} (${amount})` })),

investorDataWithId = investors => investors.map(({ id, name }) => ({ value: id, title: name })),

CardType = {
  SMALL: 'SMALL',
  LARGE: 'LARGE'
},

FrequencyType = {
  DAYS: 'Days',
  WEEKS: 'Weeks',
  MONTHS: 'Months',
  YEARS: 'Years',
},

allFrequencyTypes = () => {

  const data = []
  for (const key in FrequencyType) {
    if (Object.hasOwnProperty.call(FrequencyType, key)) {
      data.push({ title: FrequencyType[key], value: key })
    }
  }

  return data
},

getFrequencyText = type => Object.hasOwnProperty.call(FrequencyType, type) ? FrequencyType[type] : '',

RevenueType = {
  FIXED_RATE: 'FIXED_RATE',
  SHARING: 'SHARING',
},

InvestorPaymentType = {
  BANK: 'BANK',
  WALLET: 'WALLET',
},

InvestorPaymentTypes = [
  { value: InvestorPaymentType.BANK, title: 'BANK' },
  { value: InvestorPaymentType.WALLET, title: 'WALLET' },
],

LoanGroupEnum = {
  NONE: 'NONE',
  DEFAULTED: 'DEFAULTED',
},

compareStrings = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }),

sortGLAccounts = data => data.sort((a, b) => compareStrings(a.name, b.name)),

sortBundles = data => data.sort((a, b) => compareStrings(a.name, b.name)),

sortBusinesses = data => data.sort((a, b) => compareStrings(a.name, b.name)),

sortBatches = data => data.sort((a, b) => compareStrings(a.name, b.name)),

sortBuckets = data => data.sort((a, b) => compareStrings(a.name, b.name)),

sortInvestors = data => data.sort((a, b) => compareStrings(a.name, b.name)),

DEFAULT_NAV_PAGE = 1,

DEFAULT_NAV_SIZE = 25,

DEFAULT_NAV_SIZE_50 = 50,

NumberComparator = {
  NONE: 'NONE',
  EQ: 'EQ',
  LT: 'LT',
  LTE: 'LTE',
  GT: 'GT',
  GTE: 'GTE',
  DIFF: 'DIFF'
},

getDefaultPagedResult = () => ({
  content: [],
  currentPage: 0,
  pageSize: DEFAULT_NAV_SIZE,
  totalElements: 0,
  hasNext: false
}),

// Convert BootstrapTable comparator to String
getDateComparatorStr = cmp => {
  switch (cmp) {
    case '<':
      return NumberComparator.LT
    case '<=':
      return NumberComparator.LTE
    case '=':
      return NumberComparator.EQ
    case '>':
      return NumberComparator.GT
    case '>=':
      return NumberComparator.GTE
    case '!=':
      return NumberComparator.DIFF
    default:
      return NumberComparator.NONE
  }
},

GROUP_LIST = {
  ALL: 'ALL',
  DEFAULTED: 'DEFAULTED'
},

getGroupFromQueryString = group => group && group.toUpperCase() === GROUP_LIST.DEFAULTED
  ? GROUP_LIST.DEFAULTED
  : GROUP_LIST.ALL,

/**
 * Get the LoanStatus type user should see
 */
getStatusesByUserRole = (authenticated) => {

  switch (authenticated.getAuthority()) {

    case UserRoles.ADMIN:
      return LoanStatus

    case UserRoles.ACCOUNTANT:
    case UserRoles.PAYMENT_MANAGER:
      return {
        COMPLETED: 'COMPLETED'
      }

    case UserRoles.APPROVER:
      return {
        UNDERWRITER_APPROVED: 'UNDERWRITER_APPROVED',
        APPROVER_REVIEW: 'APPROVER_REVIEW',
      }

    case UserRoles.UNDERWRITER:
      return {
        NEW: 'NEW',
        UNDERWRITER_REVIEW: 'UNDERWRITER_REVIEW',
      }
    
    default: 
      return {}
  }
},

scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

export {
  INVALID_TOKEN,
  getFrequencyType,
  LoanStatus,
  VALIDATION_TYPE,
  UserRoles,
  statusColorStyle,
  HTTP_VERB,
  fetchJson,
  fetchFormData,
  fetchResource,
  fetchUrlencoded,
  parseFetchError,
  formatCollateralName,
  LoanCollateral,
  LoanCollateralEnum,
  maskEmail,
  isEmptyObject,
  fillingZeroUtility,
  renderLoanCollateral,
  getCustomerFullName,
  fineractToDate,
  fineractDateToStr,
  lowerCamelCase,
  trimSpace,
  customerFieldsObject,
  customerImageType,
  isCustomerDateField,
  validateTextState,
  ReportAPI,
  getPercentAmount,
  getUpfrontPaymentAndCurrency,
  Orientation,
  LoanRecordingStep,
  CustomerType,
  getBusinessName,
  getPlural,
  ALL_STATUS,
  REJECTED_STATUS,
  ACCEPTED_STATUS,
  DEVICE_OP_TYPE,
  isValidPhoneNumber,
  validatePhoneNumber,
  getAccessToken,
  DATE_FORMAT_STR,
  DEFAULT_NB_DAYS_EXTENSION,
  formatCurrency,
  ChargeTimeType,
  ChargeCalculationType,
  validateAmountState,
  validatePercentState,
  Currencies,
  getChargeAmount,
  capitalFirstCharacter,
  validateDateState,
  AccountUsage,
  GLAccountType,
  GLAccountTypes,
  GLAccountUsages,
  getOrganization,
  saveOrganization,
  saveDefaultCurrency,
  getDefaultCurrency,
  CALCULATION_TYPE,
  filterGLAccounts,
  glAccountsDataWithId,
  glAccountsDataWithFineractId,
  filterGLAccountsByType,
  TransactionEntryType,
  canUserSeeThisLoan,
  addUniqueValue,
  convertToObject,
  selectFiltersFromStore,
  BooleanStatuses,
  sortDate,
  sortNumber,
  validateCommissions,
  getAvatarUri,
  saveFilterConds,
  getFilterConds,
  businessDataWithId,
  batchDataWithId,
  bucketDataWithId,
  investorDataWithId,
  CardType,
  validateMinMaxState,
  FrequencyType,
  allFrequencyTypes,
  getFrequencyText,
  RevenueType,
  InvestorPaymentType,
  InvestorPaymentTypes,
  LoanGroupEnum,
  sortGLAccounts,
  sortBundles,
  sortBusinesses,
  sortBatches,
  sortBuckets,
  sortInvestors,
  DEFAULT_NAV_PAGE,
  DEFAULT_NAV_SIZE,
  DEFAULT_NAV_SIZE_50,
  getDefaultPagedResult,
  getDateComparatorStr,
  GROUP_LIST,
  NumberComparator,
  getGroupFromQueryString,
  getStatusesByUserRole,
  scrollToTop,
  formatCurrencyAlt,
}

/* ============================================================
   DATE HELPERS  (replaces date-fns)
  ============================================================ */
export function padZero(n) {
  return n < 10 ? '0' + n : '' + n
}

export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isToday(date) {
  return isSameDay(date, new Date())
}

export function isYesterday(date) {
  var y = new Date()
  y.setDate(y.getDate() - 1)
  return isSameDay(date, y)
}

export function formatDistanceToNow(date) {
  var now = new Date()
  var diff = Math.floor((now - date) / 1000)
  if (diff < 5) return 'just now'
  if (diff < 60) return diff + ' seconds ago'
  var mins = Math.floor(diff / 60)
  if (mins < 60) return mins + (mins === 1 ? ' minute ago' : ' minutes ago')
  var hrs = Math.floor(mins / 60)
  if (hrs < 24) return hrs + (hrs === 1 ? ' hour ago' : ' hours ago')
  var days = Math.floor(hrs / 24)
  if (days < 7) return days + (days === 1 ? ' day ago' : ' days ago')
  var weeks = Math.floor(days / 7)
  if (weeks < 5) return weeks + (weeks === 1 ? ' week ago' : ' weeks ago')
  var months = Math.floor(days / 30)
  if (months < 12) return months + (months === 1 ? ' month ago' : ' months ago')
  var years = Math.floor(days / 365)
  return years + (years === 1 ? ' year ago' : ' years ago')
}

var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export function formatDateLabel(date) {
  var time = padZero(date.getHours()) + ':' + padZero(date.getMinutes())
  if (isToday(date)) return formatDistanceToNow(date)
  if (isYesterday(date)) return 'Yesterday at ' + time
  return MONTHS[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear() + ' · ' + time
}

/* ============================================================ */

/**
 * Sleep x milliseconds
 */
export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Sanitize the notification text
 */
export const sanitizeNotification = input => {
  if (typeof input !== 'string') return input
  let out = input.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
  return out
}

/**
 * Truncate text to length
 */
export const truncateText = (text, length) => (!text || text.length <= length) ? text : text.substring(0, length) + '...'

/** Five minutes in milliseconds */
export const FIVE_MINUTES_MS = 5 * 60 * 1000

/**
 * Format file size to human readable text
 * @param {*} bytes 
 * @returns xx B|KB|MB
 */
export const formatBytes = (bytes) => {
  if (bytes == null || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Build a file name for report downloads
 * @returns report-name-subname-YYYY-MM-DDTHH_mm
 */
export const getReportFileName = (reportName, outputType, dateT = null) => {

  const ext = outputType ? outputType.toLowerCase() : 'txt',
  dateC = moment(dateT || Date.now()).format('YYYY-MM-DDTHH_mm'),
  name = (reportName || '')
    .toLowerCase()
    .replaceAll(' + ', '+')
    .replaceAll(' ', '-')
  return `${name}-${dateC}.${ext}`
}

/**
 * Download a blob as a file
 * @param {*} blob 
 * @param {*} filename 
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob),
  link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}



// WEBPACK FOOTER //
// ./src/lib/utils.js